import { ChevronDownIcon } from '@heroicons/react/24/outline';
import {
  Content,
  Item,
  Menu,
  Root as MenuRoot,
  Trigger,
} from '@radix-ui/react-menubar';
import {
  Item as ToggleGroupItem,
  Root as ToggleGroup,
} from '@radix-ui/react-toggle-group';
import { motion } from 'framer-motion';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { twMerge } from 'tailwind-merge';

import { TextStyle } from '../../../DesignSystem/Feedback/TextStyle';
import { SearchTermCTA } from './SearchTermCTA';

export type FilterTabsProps<T extends string> = {
  searchTerm: string;
  filter: T;
  aggregations?: Record<T, number> | undefined;
  onFilterChange: (filter: T) => void;
  onSearchTermClick?: () => void;
  filterOptions: { label: string; value: T }[];
  headerRenderer?: () => JSX.Element;
  stickyFiltersBreakpoint: number;
  wrapperClassName?: string;
  isSticky?: boolean;
  tabItemClassName?: string;
};

const PaddingAroundFilterButtonsContainer = 64;
const MaxWidthOfFilterButtonWithoutCount = 95;
const MaxWidthOfFilterButtonWithCount: Record<number, number> = {
  1: 100,
  2: 105,
  3: 110,
};

const filtersSectionMessages = defineMessages({
  moreFilter: {
    defaultMessage: 'More',
    id: 'I5NMJ8',
  },
  introJSFiltersTitle: {
    defaultMessage: 'Quick filters',
    id: 'YThexu',
  },
  introJSFiltersMsg: {
    defaultMessage:
      'Not in the mood to search or scroll? Shortcut your journey with quick filters.',
    id: 'a5Sv2S',
  },
});

export function FilterTabs<T extends string>({
  searchTerm,
  filter,
  aggregations,
  onFilterChange,
  onSearchTermClick,
  filterOptions,
  headerRenderer,
  stickyFiltersBreakpoint,
  wrapperClassName,
  tabItemClassName,
  isSticky = false,
}: FilterTabsProps<T>) {
  const { formatMessage } = useIntl();

  const [isCondensed, setIsCondensed] = useState(false);
  const [noOfCondensedOptionsToBeShown, setNoOfCondensedOptionsToBeShown] =
    useState(0);

  const handleResize = useCallback(() => {
    if (
      stickyFiltersRef.current &&
      stickyFiltersRef.current.clientWidth < stickyFiltersBreakpoint
    ) {
      setIsCondensed(true);

      const hasCountText =
        Object.values(aggregations ?? {}).some(
          (count: unknown) => typeof count === 'number' && count > 0
        ) || searchTerm.length > 0;

      const maxCount = Math.max(
        ...Object.values(aggregations ?? {}).map((count: unknown) =>
          typeof count === 'number' ? count : 0
        )
      );

      const widthOfEachButton = hasCountText
        ? MaxWidthOfFilterButtonWithCount[maxCount > 3 ? 3 : maxCount]
        : MaxWidthOfFilterButtonWithoutCount;
      const noOfButtons =
        Math.floor(
          (stickyFiltersRef.current.clientWidth -
            PaddingAroundFilterButtonsContainer) /
            widthOfEachButton
        ) - 1;
      setNoOfCondensedOptionsToBeShown(noOfButtons);
    } else {
      setIsCondensed(false);
      setNoOfCondensedOptionsToBeShown(0);
    }
  }, [aggregations, searchTerm.length, stickyFiltersBreakpoint]);

  const stickyFiltersRef = useRef<HTMLDivElement>(null);
  const prevPosition = useRef(0);
  const [showGradient, setShowGradient] = useState(false);

  const handleScroll = useCallback(() => {
    if (stickyFiltersRef.current) {
      const currentPosition = window.scrollY;

      if (currentPosition > prevPosition.current) {
        // scroll down, hide filters
        stickyFiltersRef.current.style.top = '-100px';
        setShowGradient(false);
      } else {
        const filtersRect = stickyFiltersRef.current.getBoundingClientRect();
        setShowGradient(filtersRect.y === 56);
        if (currentPosition > 400) {
          // scroll up, show filters
          stickyFiltersRef.current.style.top = '56px'; // header height - filter top padding
        }
      }
      prevPosition.current = currentPosition;
    }
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, [handleResize, handleScroll]);

  const filters = useMemo(() => {
    const allFilters = filterOptions;

    const selectedFilter = allFilters.find(
      (current) => current.value === filter
    );

    // condensedFilters represents the filter options displayed as a dropdown
    // displayFilters includes individual filter options to be shown based on the provided parameters
    // selectedFilter is the current filter selected that is present as a search param in the URL.
    // selectedFilter is displayed as a separate option beside the displayFilters.
    // Applicable only for responsive view

    // Depending on how many items can be shown on responsive view, without going to a new line
    // we show the first few items as individual options using shownFilters
    const shownFilters = allFilters
      .slice(0, noOfCondensedOptionsToBeShown)
      .filter((current) => current.value !== selectedFilter?.value);

    // The rest of the items are shown as a dropdown in condensed format
    const condensedFilters = allFilters
      .slice(noOfCondensedOptionsToBeShown, allFilters.length)
      .filter((current) => current.value !== selectedFilter?.value);

    return {
      displayFilters: isCondensed
        ? [
            // Always show ALL filter in the beginning
            ...(selectedFilter?.value === 'all' ? [selectedFilter] : []),
            ...shownFilters,
            ...(selectedFilter && selectedFilter.value !== 'all'
              ? [selectedFilter]
              : []),
          ]
        : allFilters,
      condensedFilters: condensedFilters,
    };
  }, [filter, isCondensed, noOfCondensedOptionsToBeShown, filterOptions]);

  const stickyClasses = isSticky
    ? 'sticky top-0 z-[1] px-4 pt-4 transition-[top] duration-300'
    : '';

  const gradientClasses = showGradient
    ? 'from-gray-1 to-transparent after:absolute after:-bottom-4 after:left-4 after:h-4 after:w-[calc(100%-2rem)] after:bg-gradient-to-b lg:after:left-[88px] lg:after:w-[calc(100%-11rem)]'
    : 'rounded-lg';

  return (
    <motion.section
      ref={stickyFiltersRef}
      className={twMerge('top-0 z-[1]', stickyClasses)}
    >
      <div
        className={twMerge(
          'bg-gray-1 p-4 dark:bg-gray-9',
          gradientClasses,
          wrapperClassName
        )}
      >
        {searchTerm && onSearchTermClick && (
          <SearchTermCTA
            searchTerm={searchTerm}
            onSearchTermClick={onSearchTermClick}
          />
        )}
        <div
          className="inline-block"
          data-step={2}
          data-title={formatMessage(filtersSectionMessages.introJSFiltersTitle)}
          data-intro={formatMessage(filtersSectionMessages.introJSFiltersMsg)}
        >
          <div className="mb-4">{headerRenderer?.()}</div>
          <ToggleGroup
            className="mb-1 flex text-base font-medium"
            value={filter}
            onValueChange={(value) => {
              // Toggle group allows us to deselect an item, so we need to prevent that
              if (value) {
                onFilterChange(value as T);
              }
            }}
            orientation="horizontal"
            type="single"
          >
            {filters.displayFilters.map(({ value, label }) => (
              <ToggleGroupItem
                data-testid={`discover-filter-button-${value}`}
                key={value}
                value={value}
                className={twMerge(
                  'mx-1 rounded-lg border-[1px] px-3 py-1 text-gray-9 first:ml-0',
                  'focus-visible:border-gray-9 focus-visible:outline-none focus-visible:ring focus-visible:ring-primary-6 focus-visible:ring-offset-1',
                  'data-[state=off]:border-gray-5  data-[state=off]:hover:bg-gray-3',
                  'data-[state=on]:border-gray-9 data-[state=on]:bg-gray-9 data-[state=on]:text-gray-1',
                  tabItemClassName
                )}
              >
                {label.trim()}
                {aggregations && (
                  <TextStyle variant="base-regular" as="span">
                    {` ${aggregations[value]}`}
                  </TextStyle>
                )}
              </ToggleGroupItem>
            ))}

            {isCondensed && (
              // TODO: Refactor this to use the DropdownMenu component "after" refactoring the DropdownMenu component (https://joinassembly.atlassian.net/browse/APP-11434)
              <MenuRoot>
                <Menu>
                  <Trigger
                    className={twMerge(
                      'mx-1 ml-2 flex items-center gap-2 rounded-lg border-[1px] px-3 py-1 text-gray-9',
                      'focus-visible:border-gray-9 focus-visible:outline-none focus-visible:ring focus-visible:ring-primary-6 focus-visible:ring-offset-1',
                      'data-[state=closed]:border-gray-5 data-[state=closed]:hover:bg-gray-3',
                      'data-[state=open]:border-gray-7 data-[state=open]:bg-gray-9 data-[state=open]:text-gray-1',
                      tabItemClassName
                    )}
                  >
                    {formatMessage(filtersSectionMessages.moreFilter)}
                    <ChevronDownIcon className="h-4 w-4" />
                  </Trigger>
                  <Content
                    className="space-b focus:outline-noneshadow-md z-1 absolute left-0 z-10 mt-1 max-h-[32rem] w-32 origin-top transform overflow-auto rounded-md border border-gray-5 bg-gray-1 py-1 shadow-lg-down ring-opacity-5"
                    align="start"
                    sideOffset={5}
                    alignOffset={-3}
                  >
                    {filters.condensedFilters.map(({ value, label }) => (
                      <Item
                        data-testid={`discover-condensed-filter-menu-item-${value}`}
                        key={value}
                        onSelect={() => {
                          onFilterChange(value);
                        }}
                        className="cursor-pointer focus:bg-primary-2 focus:outline-none"
                      >
                        <button className="bg-gray-0 flex w-full cursor-pointer items-center justify-between p-[9px] pr-2 text-sm text-gray-8 hover:bg-gray-3 focus:outline-none">
                          {label}
                        </button>
                      </Item>
                    ))}
                  </Content>
                </Menu>
              </MenuRoot>
            )}
          </ToggleGroup>
        </div>
      </div>
    </motion.section>
  );
}
